// Pipeline de migração synapse_os_v3 (localStorage HTML) → ProseMirror JSON + API.
// Passos:
//   1. Lê snapshot do localStorage.
//   2. Para cada nó, sanitiza HTML com DOMPurify (allowlist alinhada a Tiptap).
//   3. Converte HTML → ProseMirror JSON (DOMParser + schema mínimo).
//   4. Detecta [[wiki-link]] e gera marks + pendências de resolução.
//   5. Imagens base64 inline → upload via presigned MinIO; substitui src.
//   6. Cria documents na API com `parentId` e `position` preservados.
//   7. Cria links após todos os docs existirem.

import DOMPurify from 'dompurify';
import { api } from './api.js';
import type { Document } from './queries.js';

interface LegacyNode {
  id: string;
  parentId: string | null;
  label: string;
  content: string; // HTML cru
  innerImage: string | null;
  type: string;
  category?: string;
  color: string;
  x: number;
  y: number;
  size: number;
  locked?: boolean;
  icon?: string;
}

interface LegacyLink {
  id: string;
  source: string;
  target: string;
  type?: string;
}

interface LegacySnapshot {
  nodes: LegacyNode[];
  links: LegacyLink[];
  focusedParentId: string | null;
}

const ALLOW_TAGS = ['p', 'h1', 'h2', 'h3', 'ul', 'ol', 'li', 'strong', 'em', 'code', 'pre', 'blockquote', 'a', 'br'];
const ALLOW_ATTR = ['href'];

export interface ImportReport {
  total: number;
  success: number;
  failed: Array<{ id: string; error: string }>;
  legacyMapToNew: Map<string, string>; // legacyId -> newDocumentId
}

export function loadLegacySnapshot(): LegacySnapshot | null {
  try {
    const raw = localStorage.getItem('synapse_os_v3');
    if (!raw) return null;
    return JSON.parse(raw) as LegacySnapshot;
  } catch {
    return null;
  }
}

function sanitizeHtml(html: string): string {
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: ALLOW_TAGS,
    ALLOWED_ATTR: ALLOW_ATTR,
  });
}

// HTML → ProseMirror JSON mínimo. Tiptap parsea melhor via DOMParser, mas
// para o importer um JSON intermediário simples basta.
function htmlToProseMirror(html: string): { type: 'doc'; content: unknown[] } {
  const sanitized = sanitizeHtml(html);
  const tmp = document.createElement('div');
  tmp.innerHTML = sanitized;

  const content: unknown[] = [];

  for (const child of Array.from(tmp.childNodes)) {
    const node = nodeFromDom(child);
    if (node) content.push(node);
  }
  if (content.length === 0) content.push({ type: 'paragraph' });
  return { type: 'doc', content };
}

function nodeFromDom(el: ChildNode): unknown {
  if (el.nodeType === Node.TEXT_NODE) {
    const text = el.textContent ?? '';
    if (!text.trim()) return null;
    return { type: 'paragraph', content: textToInline(text) };
  }
  if (el.nodeType !== Node.ELEMENT_NODE) return null;
  const e = el as HTMLElement;
  const tag = e.tagName.toLowerCase();
  switch (tag) {
    case 'p':
      return { type: 'paragraph', content: inlineChildren(e) };
    case 'h1':
    case 'h2':
    case 'h3':
      return { type: 'heading', attrs: { level: Number(tag[1]) }, content: inlineChildren(e) };
    case 'ul':
      return { type: 'bulletList', content: Array.from(e.children).map(liToNode) };
    case 'ol':
      return { type: 'orderedList', content: Array.from(e.children).map(liToNode) };
    case 'blockquote':
      return { type: 'blockquote', content: [{ type: 'paragraph', content: inlineChildren(e) }] };
    case 'pre':
      return { type: 'codeBlock', content: [{ type: 'text', text: e.textContent ?? '' }] };
    default:
      return { type: 'paragraph', content: inlineChildren(e) };
  }
}

function liToNode(li: Element): unknown {
  return { type: 'listItem', content: [{ type: 'paragraph', content: inlineChildren(li) }] };
}

function inlineChildren(el: Element): unknown[] {
  const out: unknown[] = [];
  for (const c of Array.from(el.childNodes)) {
    if (c.nodeType === Node.TEXT_NODE) {
      out.push(...textToInline(c.textContent ?? ''));
    } else if (c.nodeType === Node.ELEMENT_NODE) {
      const ce = c as HTMLElement;
      const tag = ce.tagName.toLowerCase();
      const text = ce.textContent ?? '';
      if (!text) continue;
      const marks: unknown[] = [];
      if (tag === 'strong' || tag === 'b') marks.push({ type: 'bold' });
      if (tag === 'em' || tag === 'i') marks.push({ type: 'italic' });
      if (tag === 'code') marks.push({ type: 'code' });
      if (tag === 'a') marks.push({ type: 'link', attrs: { href: ce.getAttribute('href') } });
      out.push({ type: 'text', text, marks: marks.length ? marks : undefined });
    }
  }
  return out;
}

// Texto puro com detecção de [[wiki-link]].
function textToInline(text: string): unknown[] {
  const out: unknown[] = [];
  const re = /\[\[([^\]]+)\]\]/g;
  let last = 0;
  let m;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) out.push({ type: 'text', text: text.slice(last, m.index) });
    out.push({
      type: 'text',
      text: m[1],
      marks: [{ type: 'wikiLink', attrs: { target: m[1] } }],
    });
    last = re.lastIndex;
  }
  if (last < text.length) out.push({ type: 'text', text: text.slice(last) });
  return out.length ? out : [{ type: 'text', text }];
}

// Upload de imagem base64 inline para MinIO.
async function uploadBase64Image(
  workspaceId: string,
  dataUrl: string,
): Promise<string | null> {
  try {
    const m = dataUrl.match(/^data:([^;]+);base64,(.+)$/);
    if (!m) return null;
    const mime = m[1]!;
    const blob = atob(m[2]!);
    const bytes = new Uint8Array(blob.length);
    for (let i = 0; i < blob.length; i++) bytes[i] = blob.charCodeAt(i);
    const sizeBytes = bytes.byteLength;

    const presign = await api<{ assetId: string; uploadUrl: string }>('/assets/upload', {
      method: 'POST',
      body: { workspaceId, mime, sizeBytes, filename: `legacy.${mime.split('/')[1] ?? 'bin'}` },
    });
    await fetch(presign.uploadUrl, {
      method: 'PUT',
      headers: { 'content-type': mime },
      body: bytes,
    });
    return presign.assetId;
  } catch {
    return null;
  }
}

export interface ImportProgress {
  step: 'reading' | 'documents' | 'links' | 'done';
  current: number;
  total: number;
  message?: string;
}

export async function importLegacy(
  workspaceId: string,
  onProgress: (p: ImportProgress) => void,
): Promise<ImportReport> {
  const snap = loadLegacySnapshot();
  const report: ImportReport = {
    total: 0,
    success: 0,
    failed: [],
    legacyMapToNew: new Map(),
  };
  if (!snap) {
    onProgress({ step: 'done', current: 0, total: 0, message: 'localStorage vazio' });
    return report;
  }

  // Topological-ish: parents primeiro (parentId null), depois filhos.
  const sorted = [...snap.nodes].sort((a, b) => {
    if (a.parentId === null && b.parentId !== null) return -1;
    if (a.parentId !== null && b.parentId === null) return 1;
    return 0;
  });

  report.total = sorted.length;
  onProgress({ step: 'documents', current: 0, total: sorted.length });

  for (let i = 0; i < sorted.length; i++) {
    const n = sorted[i]!;
    try {
      // innerImage base64 → upload (best-effort).
      let imageAssetId: string | null = null;
      if (n.innerImage?.startsWith('data:')) {
        imageAssetId = await uploadBase64Image(workspaceId, n.innerImage);
      }

      const content = htmlToProseMirror(n.content ?? '');
      const parentNewId = n.parentId ? (report.legacyMapToNew.get(n.parentId) ?? null) : null;
      const cat = (n.category as 'strategy' | 'idea' | 'execution' | undefined) ?? 'idea';

      const created = await api<Document>(`/workspaces/${workspaceId}/documents`, {
        method: 'POST',
        body: {
          parentId: parentNewId,
          title: n.label,
          icon: n.icon ?? null,
          color: n.color,
          category: cat,
          type: (n.type as 'idea' | 'text' | 'image' | 'url' | 'universe' | 'doc') ?? 'idea',
          isChildPage: parentNewId !== null,
          content,
          position: { x: n.x, y: n.y, size: n.size, locked: n.locked ?? false },
        },
      });

      report.legacyMapToNew.set(n.id, created.id);
      if (imageAssetId) {
        // TODO: re-injetar referência de imagem no content. MVP guarda como meta.
      }
      report.success++;
    } catch (err) {
      report.failed.push({ id: n.id, error: err instanceof Error ? err.message : 'unknown' });
    }
    onProgress({ step: 'documents', current: i + 1, total: sorted.length });
  }

  // Links.
  onProgress({ step: 'links', current: 0, total: snap.links.length });
  for (let i = 0; i < snap.links.length; i++) {
    const lk = snap.links[i]!;
    const sourceNew = report.legacyMapToNew.get(lk.source);
    const targetNew = report.legacyMapToNew.get(lk.target);
    if (!sourceNew || !targetNew) continue;
    try {
      await api(`/workspaces/${workspaceId}/links`, {
        method: 'POST',
        body: { sourceId: sourceNew, targetId: targetNew, type: lk.type ?? 'synapse' },
      });
    } catch {
      /* unique violation = OK */
    }
    onProgress({ step: 'links', current: i + 1, total: snap.links.length });
  }

  onProgress({ step: 'done', current: report.success, total: report.total });
  return report;
}
